<template>
  <div class="page page-settings content">
    <h1 class="page__title">{{ t('settings.menuTitle') }}</h1>
    <div class="notification-settings" v-if="showNotificationSettings">
      <p>{{ t('global.notifications') }}</p>
      <Toggle
        v-model="pushNotificationsEnabled"
        @change="notificationsSettingChanged($event)"
      ></Toggle>
    </div>
    <div class="email-notification-settings">
      <p>{{ t('global.emailNotifications') }}</p>
      <Toggle
        v-model="emailSubscription"
      ></Toggle>
    </div>
    <div class="highcontrast-settings">
      <p>{{ t('settings.highContrast') }}</p>
      <Toggle
        v-model="highContrast"
        :height="30"
      ></Toggle>
    </div>
    <div v-if="hasProduction && !isCommunityless" >
      <h3 class="selling-prices">
        {{ t('settings.sellingPriceTitle') }}
        <Spinner v-if="!defaultSellingPricesLoaded" />
      </h3>
      <ProducerSettings />
    </div>


    <div class="watchface-settings" v-if="!isCommunityless">
      <h3>{{ t('settings.watchFaceTitle') }}</h3>
      <div class="watchface-settings--description">
        {{ t('settings.watchFace') }}
        <a
          target="_blank"
          href="https://www.efriends.at/images/besserer-strom/EFR_Watch_Manual_Website.pdf"
        >hier <External /></a
        >.
      </div>
      <Choice
        name="watchface"
        :default="watchFace"
        :items="watchFaces"
        @selection-changed="onWatchFaceChanged($event)"
      />

      <div class="watchface-settings--description">
        {{ t('settings.watchFaceBrightness') }}
      </div>

      <Choice
        name="watchfacesunny"
        :default="watchFaceAuto?.toString()"
        :items="watchFaceSunny"
        @selection-changed="onWatchFaceSunnyChanged($event)"
      /><br />
      <Choice
        name="watchfacebrightness"
        :default="watchFaceBrightness"
        :items="watchFaceBrightnesses"
        @selection-changed="onWatchFaceBrightnessChanged($event)"
      />
      <div
        class="watchface-settings--timer__switch"
        v-if="watchFaceAuto === 'fixed'"
      >
        <span
          class="watchface-settings--timer__switch-label"
          id="toggle-active-label-watch-timer"
        >
          {{ t('settings.watchFaceTimer') }}
        </span>
         <Toggle
           v-model="watchFaceTimerActive"
           aria-labelledby="toggle-active-label-watch-timer"
         />
      </div>
      <div
        class="timeslot"
        v-if="watchFaceTimerActive && watchFaceAuto === 'fixed'"
      >
        <div class="timeslot__slot">
          <input
            type="number"
            min="0"
            max="23"
            v-model="watchFaceTimerStart"
            class="timeslot__input"
          />
          <span class="timeslot__until">-</span>
          <input
            type="number"
            min="1"
            max="24"
            v-model="watchFaceTimerEnd"
            class="timeslot__input"
          />
        </div>
      </div>

      <div
        class="watchface-settings--timer__brightness"
        v-if="watchFaceTimerActive && watchFaceAuto === 'fixed'"
      >
        <span class="watchface-settings--timer__bfightness-label">
          {{ t('settings.brightnessOutsideTime') }}
        </span>
      </div>
      <Choice
        name="watchfacetimerbrightness"
        :default="watchFaceTimerBrightness"
        :items="watchFaceBrightnesses"
        @selection-changed="onWatchFaceTimerBrightnessChanged($event)"
        v-if="watchFaceTimerActive && watchFaceAuto === 'fixed'"
      />
      <div :class="actionsClasses">
        <a class="settings-actions__cancel" @click="resetWatchFaceSettingChanges()">
          {{ t('global.cancel') }}
        </a>

        <LinkButton
          type="small"
          icon-right="Arrow"
          form="watchFaceSettings"
          :text="t('global.save')"
          :extra-classes="['button--primary', 'settings-actions__save']"
          :disable-on-submit="true"
          @click.native="saveWatchFace"
        />
      </div>
      <Alert v-if="watchFaceError" class="settings-actions__error">
        {{ t('settings.watchFaceError') }}
      </Alert>
    </div>

    <Meters  v-if="!isCommunityless" />
  </div>
</template>

<script setup lang="ts">
  import ProducerSettings from '@/components/settings/ProducerSettings.vue'
  import Toggle from '@vueform/toggle'
  import { useNotifications } from '@/composables/useNotifications.ts'
  import Meters from '@/components/settings/Meters.vue'
  import Choice from '@/components/Choice.vue'
  import LinkButton from '@/components/LinkButton.vue'
  import Alert from '@/components/Alert.vue'
  import External from '@/components/icons/External.vue'
  import Spinner from '@/components/icons/Spinner.vue'

  import { useAccountStore } from '@/stores/useAccountStore'
  import { useAppStore } from '@/stores/useAppStore'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useFormsStore } from '@/stores/useFormsStore'
  import { useSettingsStore } from '@/stores/useSettingsStore'
  import { useStrings } from '@/composables/useStrings.ts'
  import { computed, ref, watch } from 'vue'
  import { useMyCube } from '@/composables/useMyCube.js'

  const accountStore = useAccountStore()
  const appStore = useAppStore()
  const cubeStore = useCubeStore()
  const formsStore = useFormsStore()
  const settingsStore = useSettingsStore()
  const { t } = useStrings()
  const {
    setNotificationsEnabled,
    setEmailNotificationsEnabled,
  } = useNotifications()

  const defaultSellingPricesLoaded = computed(() => settingsStore.defaultSellingPricesLoaded)
  const isCommunityless = computed(() => accountStore.properties.isCommunityless)

  const pushNotificationsEnabled = ref(cubeStore.pushNotificationsEnabled);

  watch(() => cubeStore.pushNotificationsEnabled, (val) => {
    pushNotificationsEnabled.value = val;
  });

  const highContrast = computed({
    get: () => accountStore.isHighContrast,
    set: value => accountStore.setHighContrast(value)
  })

  const emailSubscription = computed({
    get: () => cubeStore.emailSubscription,
    set: value => setEmailNotificationsEnabled(value)
  })

  const { hasProduction } = useMyCube()

  const watchFace = ref(cubeStore.watchFace)
  const watchFaceBrightness = ref(cubeStore.watchFaceBrightness)
  const watchFaceTimerActive = ref(cubeStore.watchFaceTimerActive)
  const watchFaceTimerEnd = ref(cubeStore.watchFaceTimerSlot.end)
  const watchFaceTimerStart = ref(cubeStore.watchFaceTimerSlot.start)
  const watchFaceTimerBrightness = ref(cubeStore.watchFaceTimerBrightness)
  const watchFaceAuto = ref(cubeStore.watchFaceAuto)

  const watchFaceSettingsChanged = computed(() => {
    return (
      cubeStore.watchFace !== watchFace.value ||
      cubeStore.watchFaceBrightness !== watchFaceBrightness.value ||
      cubeStore.watchFaceTimerActive !== watchFaceTimerActive.value ||
      cubeStore.watchFaceTimerBrightness !== watchFaceTimerBrightness.value ||
      cubeStore.watchFaceTimerSlot.start !== watchFaceTimerStart.value ||
      cubeStore.watchFaceTimerSlot.end !== watchFaceTimerEnd.value ||
      cubeStore.watchFaceAuto !== watchFaceAuto.value
    )
  })

  const showNotificationSettings = computed(() => {
    const swRegistration = appStore.serviceWorkerRegistration
    if (
      !swRegistration ||
      !('pushManager' in swRegistration) ||
      !('Notification' in window)
    ) {
      return false
    }
    return true
  })

  const watchFaces = computed(() => {
    return [
      { title: t('settings.watchFaceClassic'), value: 'Classic' },
      { title: t('settings.watchFaceV2'), value: 'V2' }
    ]
  })

  const watchFaceSunny = computed(() => {
    return [
      { title: t('settings.watchFaceAuto'), value: 'auto' },
      { title: t('settings.watchFaceManual'), value: 'fixed' }
    ]
  })

  const watchFaceBrightnesses = computed(() => {
    return [
      { title: 'Aus', value: '0' },
      { title: '20%', value: '1' },
      { title: '40%', value: '2' },
      { title: '60%', value: '3' },
      { title: '80%', value: '4' },
      { title: '100%', value: '5' }
    ]
  })

  const actionsClasses = computed(() => {
    let classes = ['settings-actions']
    if (watchFaceSettingsChanged.value) {
      classes.push('settings-actions--visible')
    }
    return classes.join(' ')
  })

  const watchFaceError = computed(() => formsStore.getFormState('watchFaceSettings') === 'error')


  const notificationsSettingChanged = (data) => {
    if (typeof(data) == "boolean" ) {
      setNotificationsEnabled(data)
    } else{
      setNotificationsEnabled(data.value)
    }
  }

  const onWatchFaceChanged = (data) => {
    watchFace.value = data
  }

  const onWatchFaceSunnyChanged = (data) => {
    watchFaceAuto.value = data
  }

  const onWatchFaceBrightnessChanged = (data) => {
    watchFaceBrightness.value = data
  }

  const onWatchFaceTimerBrightnessChanged = (data) => {
    watchFaceTimerBrightness.value = data
  }

  const saveWatchFace = async () => {
    formsStore.setFormState('watchFaceSettings', 'submitting')
    await cubeStore.changeWatchFace({
      watchFace: watchFace.value,
      watchFaceAuto: watchFaceAuto.value,
      watchFaceBrightness: watchFaceBrightness.value,
      watchFaceTimerActive: watchFaceTimerActive.value,
      watchFaceTimerBrightness: watchFaceTimerBrightness.value,
      watchFaceTimerSlot: {
        start: watchFaceTimerStart.value,
        end: watchFaceTimerEnd.value,
      },
    })
  }

  const resetWatchFaceSettingChanges = () => {
    watchFace.value = cubeStore.watchFace
    watchFaceAuto.value = cubeStore.watchFaceAuto
    watchFaceBrightness.value = cubeStore.watchFaceBrightness
    watchFaceTimerActive.value = cubeStore.watchFaceTimerActive
    watchFaceTimerBrightness.value = cubeStore.watchFaceTimerBrightness
    watchFaceTimerEnd.value = cubeStore.watchFaceTimerSlot.end
    watchFaceTimerStart.value = cubeStore.watchFaceTimerSlot.start

    formsStore.setFormState('watchFaceSettings', false)
  }
</script>

<style lang="scss">
.page-settings {
  max-width: $content-page-width;
}

.selling-prices {
  display: flex;
  justify-content: space-between;

  .icon--spinner {
    animation: spin 1.8s linear 0s infinite;
  }
}

.highcontrast-settings,
.notification-settings,
.email-notification-settings {
  display: flex;
  justify-content: space-between;
  margin-bottom: $spacer20;
}

.watchface-settings {
  margin-top: $spacer30;
  margin-bottom: $spacer20;

  &--auto__switch,
  &--timer__switch {
    margin-top: 1.5rem;
    display: flex;
    justify-content: space-between;
  }

  .radios {
    margin-top: $spacer20;

    &__item--active {
      &-auto,
      &-fixed,
      &-Classic,
      &-V2 {
        background: $community;
        border-color: $community;
      }

      &-0 {
        background: $black;
        border-color: $black;
        color: $white;
      }
      &-1 {
        background: color.adjust($community, $alpha: 0.8);
        border-color: color.adjust($community, $alpha: 0.8);
      }
      &-2 {
        background: color.adjust($community, $alpha: 0.6);
        border-color: color.adjust($community, $alpha: 0.6);
      }
      &-3 {
        background: color.adjust($community, $alpha: 0.4);
        border-color: color.adjust($community, $alpha: 0.4);
      }
      &-4 {
        background: color.adjust($community, $alpha: 0.2);
        border-color: color.adjust($community, $alpha: 0.2);
      }
      &-5 {
        background: $community;
        border-color: $community;
      }
    }
  }
  &--description {
    margin-top: $spacer10;
  }
}
.settings-actions {
  display: flex;
  justify-content: flex-end;
  max-height: 0;
  overflow: hidden;
  transition: $transition-base;
  align-items: center;
  margin: $spacer10 0;

  &--visible {
    max-height: 200px;
  }

  &__cancel {
    cursor: pointer;
    margin-right: $spacer10;
  }
}

.icon {
  width: 1em;
  vertical-align: middle;
}

.timeslot {
  cursor: default;
  position: relative;
  margin-top: $spacer10;
  margin-bottom: $spacer10;
  justify-content: space-between;

  @media (min-width: $bp-ssm) {
    display: flex;
    margin-bottom: $spacer20;
    flex-wrap: wrap;
  }

  &__title {
    padding-top: $spacer05;
    margin-bottom: $spacer025;
    margin-right: $spacer10;
    width: 7rem;
    flex-basis: 100%;
  }

  &__slot {
    flex-grow: 1;
  }

  &__slot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    @media (min-width: $bp-md) {
      margin-bottom: $spacer05;
    }
  }

  &__input[type='number'] {
    width: 35%;
    flex-shrink: 0;
    padding: $spacer025;
    // Prevent webkit browsers from cutting of the displayed value.
    min-width: 6rem;
  }

  &__until {
    margin: 0 $spacer05;
  }
}
</style>
